import {
  canParseOperationHours,
  parseAndOrderWeekdays,
  isOpen,
  toLocalDate,
  getBoundaryMinutes,
  getOpenTimeLabel,
} from '@utils/operationHours.ts';

import { PoiOpenStatus, type PoiOpenStatusType } from '../../../utils/constants.ts';

interface Poi {
  state?: string;
  operationHours?: string;
  dynamicData?: {
    'open-closed-status'?: {
      isOpen: boolean;
      expiresAt?: number;
    };
  };
}

/** Translation function, matching the shape supplied by the plugin runtime. */
type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

/**
 * The resolved open/closed state for a POI at a point in time.
 *
 * `status` is a locale-independent enum and is the ONLY thing UI should branch on
 * (for colour, icon, etc). `timeLabel` is already translated and is for display only -
 * never parse it or match substrings against it.
 */
export interface PoiOpenStatusResult {
  isOpen: boolean;
  status: PoiOpenStatusType;
  timeLabel?: string;
}

/** A boundary within this many minutes counts as "opening soon" / "closing soon". */
const SOON_THRESHOLD_MINUTES = 60;

/**
 * Computes whether a POI is currently open based on its state, dynamic data, and operation hours.
 *
 * This is the SINGLE SOURCE OF TRUTH for open/closed status across the entire application.
 * The result is deliberately NOT cached on the POI: it depends on the current time, so callers
 * must evaluate it on demand (see the `poi/getOpenStatus` bus message).
 *
 * Return values:
 * - `true` if the POI is currently open
 * - `false` if the POI is currently closed (including permanently closed)
 * - `undefined` if the status cannot be determined (missing/unparseable hours, missing timezone)
 *
 * The evaluation order is:
 * 1. If poi.state === 'permanently-closed' => false
 * 2. If dynamicData['open-closed-status'] exists and is not expired => use its isOpen value
 * 3. If operationHours can be parsed and a venue timezone is known => evaluate against venue-local time
 * 4. Otherwise => undefined (cannot determine)
 *
 * @param poi - The POI object to evaluate
 * @param venueTimezone - IANA timezone string (e.g., "America/New_York"). REQUIRED for step 3 - no fallback to browser timezone.
 * @param now - Current time to evaluate against (defaults to new Date())
 * @returns boolean | undefined
 */
export function computeIsOpenNow(
  poi: Poi,
  venueTimezone: string | undefined | null,
  now: Date = new Date(),
): boolean | undefined {
  // Permanently closed POIs are always closed, regardless of hours or timezone
  if (poi.state === 'permanently-closed') {
    return false;
  }

  // Check for unexpired dynamic data override. This is authoritative and needs no timezone,
  // since expiresAt is an absolute epoch timestamp.
  const dynamicStatus = poi.dynamicData?.['open-closed-status'];
  if (dynamicStatus) {
    const expiresAt = dynamicStatus.expiresAt;
    // If no expiry or not yet expired, use the dynamic value
    if (expiresAt === undefined || expiresAt > now.getTime()) {
      return dynamicStatus.isOpen;
    }
    // Otherwise fall through to hours parsing (expired override)
  }

  // Evaluating operation hours requires a venue timezone; without one we cannot
  // reliably determine status.
  if (!venueTimezone) {
    return undefined;
  }

  // Check if we can parse the operation hours
  if (!canParseOperationHours(poi.operationHours)) {
    return undefined;
  }

  // Localize "now" to the venue timezone before evaluating hours, so that hours are
  // interpreted in the venue's local time rather than the browser's.
  const localizedNow = toLocalDate(now, venueTimezone);

  // Parse and evaluate the operation hours
  const weekdayPeriods = parseAndOrderWeekdays(poi.operationHours, localizedNow);
  if (!weekdayPeriods || weekdayPeriods.length === 0) {
    return undefined;
  }

  return isOpen(weekdayPeriods, localizedNow);
}

/**
 * Resolves the full open/closed presentation state for a POI.
 *
 * This is the function UI consumers should get their data from (via the `poi/getOpenStatus`
 * bus message). It layers two things on top of {@link computeIsOpenNow}:
 *
 * 1. `status` - a locale-independent {@link PoiOpenStatus} value, including the
 *    "opening soon" / "closing soon" refinements. UI must branch on this, never on `timeLabel`.
 * 2. `timeLabel` - an already-translated display string such as "Closing in 30 minutes".
 *
 * Like `computeIsOpenNow`, the result is deliberately NOT cached on the POI: it depends on
 * the current time and must be re-evaluated whenever the view is (re)built.
 *
 * @returns the resolved status, or `undefined` when it cannot be determined.
 */
export function computePoiOpenStatus(
  poi: Poi,
  venueTimezone: string | undefined | null,
  T: TranslateFn,
  now: Date = new Date(),
): PoiOpenStatusResult | undefined {
  const openNow = computeIsOpenNow(poi, venueTimezone, now);
  if (openNow === undefined) {
    return undefined;
  }

  const status = resolveStatus(poi, venueTimezone, openNow, now);

  // getOpenTimeLabel returns '' when it has nothing useful to say; normalise that to undefined
  // so consumers only have to check for one "no label" value.
  //
  // The label is derived purely from operation hours, so it is only truthful when the hours
  // are what decided the status. When an override decided it instead, the hours-derived label
  // would contradict the status (see isStatusOverridden), so we emit no label and let the UI
  // fall back to plain Open/Closed text.
  const timeLabel =
    venueTimezone && !isStatusOverridden(poi, now)
      ? getOpenTimeLabel(poi.operationHours, now, T, venueTimezone) || undefined
      : undefined;

  return { isOpen: openNow, status, timeLabel };
}

/**
 * True when the status came from something other than the operation hours.
 *
 * Mirrors the precedence in {@link computeIsOpenNow}: the permanently-closed state and an
 * unexpired dynamic-data override both outrank the published hours. In those cases the hours
 * describe when the POI would normally be open, which can flatly contradict the real status -
 * e.g. a POI force-closed by dynamic data at noon still has hours saying "Open until 5:00 PM".
 */
function isStatusOverridden(poi: Poi, now: Date): boolean {
  if (poi.state === 'permanently-closed') {
    return true;
  }

  const dynamicStatus = poi.dynamicData?.['open-closed-status'];
  if (!dynamicStatus) {
    return false;
  }

  // An expired override is ignored by computeIsOpenNow, so the hours are authoritative again
  return dynamicStatus.expiresAt === undefined || dynamicStatus.expiresAt > now.getTime();
}

/**
 * Refines a plain open/closed boolean into an OPEN / CLOSED / OPENING_SOON / CLOSING_SOON value.
 *
 * Falls back to the plain open/closed value whenever the hours are not the thing that decided
 * the status: either they cannot be parsed, or an override outranked them. The "soon" states are
 * derived from the hours' boundaries, so inferring one from hours that did not determine the
 * status would invent information the override never supplied - e.g. a POI force-closed at 12:00
 * whose hours start at 12:30 would otherwise report "opening soon" despite being authoritatively
 * closed with no stated opening time.
 */
function resolveStatus(
  poi: Poi,
  venueTimezone: string | undefined | null,
  openNow: boolean,
  now: Date,
): PoiOpenStatusType {
  const plainStatus = openNow ? PoiOpenStatus.OPEN : PoiOpenStatus.CLOSED;

  if (isStatusOverridden(poi, now) || !venueTimezone || !canParseOperationHours(poi.operationHours)) {
    return plainStatus;
  }

  const localizedNow = toLocalDate(now, venueTimezone);
  const weekdayPeriods = parseAndOrderWeekdays(poi.operationHours, localizedNow);
  if (!weekdayPeriods || weekdayPeriods.length === 0) {
    return plainStatus;
  }

  const { minutesToOpen, minutesToClose } = getBoundaryMinutes(weekdayPeriods, localizedNow);

  if (openNow) {
    return isWithinThreshold(minutesToClose) ? PoiOpenStatus.CLOSING_SOON : PoiOpenStatus.OPEN;
  }

  return isWithinThreshold(minutesToOpen) ? PoiOpenStatus.OPENING_SOON : PoiOpenStatus.CLOSED;
}

/** True when a boundary is still ahead of us and lands inside the "soon" window. */
function isWithinThreshold(minutes: number | undefined): boolean {
  return minutes !== undefined && minutes > 0 && minutes <= SOON_THRESHOLD_MINUTES;
}
